import { Controller } from "@hotwired/stimulus"
import { errorDetail, formatDate, formatSubcatLabel, getAllJSON, sendJSON } from "../api.js"
import { comboValue, setComboOptions } from "../combobox_helpers.js"
import { setActIcon, setActLabel, setActVariant } from "../icons.js"

// Powiadomienia popytu (backorder): lista + włącz + wyłącz + usuń.
// „Wyłącz" = PATCH {is_active: false} (miękkie, odwracalne wyłączenie); „Włącz" = POST
// tych samych parametrów (API reaktywuje istniejące powiadomienie — idempotentne).
// „Usuń" = DELETE (twarde miękkie usunięcie — wiersz znika z listy).
// `organization` jest wysyłana JAWNIE: użytkownik w wielu org (i personel) wybiera
// ją z comboboxa — bez tego API odpowiada 400 (auto tylko przy dokładnie jednej org).
// Kształt BUR: subcategory + opcjonalnie voivodeship.
// Kształt Insurance: category + voivodeship (wymagane) + opcjonalnie county.
// Pola formularza to comboboxy (partials/_combobox.html) w wrapperach z dawnymi nazwami targetów;
// wartość czytamy przez comboValue(wrapper). Powiat to combobox zdalny (/api/counties/) zależny od
// województwa (depends_on) — kaskadę prowadzi combobox, nie ten kontroler.
export default class extends Controller {
  static targets = [
    "rows", "status", "subcatSelect", "voivSelect", "orgWrap", "orgSelect",
    "formError", "emailFreqGroup", "smsDailyCheck",
    "verticalTab", "burForm", "insuranceForm", "categorySelect", "insVoivSelect", "countySelect",
  ]
  static values = {
    url: String,               // /api/backorder-subscriptions/
    subcategoriesUrl: String,  // /api/subcategories/
    voivodeshipsUrl: String,   // /api/voivodeships/
    organizationsUrl: String,  // /api/organizations/
    categoriesUrl: String,     // /api/categories/
  }

  async connect() {
    this.subcatNames = new Map()
    this.voivNames = new Map()
    await this.#loadDictionaries()
    await this.load()
  }

  async load() {
    let subscriptions
    try {
      subscriptions = await getAllJSON(this.urlValue)
    } catch {
      this.#status("Nie udało się załadować powiadomień. Odśwież stronę.")
      return
    }
    this.subscriptions = new Map(subscriptions.map(sub => [String(sub.id), sub]))
    this.rowsTarget.textContent = ""
    if (!subscriptions.length) {
      this.#status("Brak powiadomień. Dodaj pierwsze powyżej.")
      return
    }
    this.#status(null)
    const template = document.getElementById("backorder-row-template")
    for (const sub of subscriptions) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.dataset.backorderRow = sub.id
      for (const radio of row.querySelectorAll('[data-field="edit-email"]')) {
        radio.name = `backorder-${sub.id}-email-frequency`
      }

      const isInsurance = sub.category != null

      // Kolumna PODKATEGORIA / KATEGORIA
      row.querySelector('[data-field="subcat"]').textContent = isInsurance
        ? (sub.category_name ?? `#${sub.category}`)
        : (this.subcatNames.get(sub.subcategory) ?? sub.subcategory_name ?? `#${sub.subcategory}`)

      // Kolumna WOJEWÓDZTWO (+ powiat dla insurance)
      if (isInsurance) {
        const voivText = this.voivNames.get(sub.voivodeship) ?? sub.voivodeship_name ?? `#${sub.voivodeship}`
        const countyText = sub.county_name ? `, pow. ${sub.county_name}` : ", dowolny powiat"
        row.querySelector('[data-field="voiv"]').textContent = voivText + countyText
      } else {
        row.querySelector('[data-field="voiv"]').textContent =
          sub.voivodeship
            ? (this.voivNames.get(sub.voivodeship) ?? sub.voivodeship_name ?? `#${sub.voivodeship}`)
            : "Dowolne"
      }

      const badge = row.querySelector('[data-field="badge"]')
      badge.className = `ls-badge ${sub.is_active ? "ls-badge--sent" : "ls-badge--new"}`
      badge.textContent = sub.is_active ? "aktywna" : "wyłączona"

      row.querySelector('[data-field="count"]').textContent = sub.notify_count
      row.querySelector('[data-field="last"]').textContent =
        sub.last_notified_at ? formatDate(sub.last_notified_at) : "—"

      const freqLabel = { daily: "raz dziennie", weekly: "raz w tygodniu", off: "wyłączony" }[sub.email_frequency] ?? sub.email_frequency
      const smsText = sub.sms_daily ? "tak" : "nie"
      row.querySelector('[data-field="channels"]').textContent = `E-mail: ${freqLabel} · SMS: ${smsText}`

      const toggle = row.querySelector('[data-field="toggle"]')
      // Wyłącz = destructive (red, power-off); Włącz = accepting (green, power)
      setActLabel(toggle, sub.is_active ? "Wyłącz" : "Włącz")
      setActVariant(toggle, sub.is_active ? "danger" : "success")
      setActIcon(toggle, sub.is_active ? "power-off" : "power")
      toggle.setAttribute("data-backorder-id-param", sub.id)
      toggle.setAttribute("data-backorder-active-param", sub.is_active)
      toggle.setAttribute("data-backorder-email-frequency-param", sub.email_frequency ?? "daily")
      toggle.setAttribute("data-backorder-sms-daily-param", sub.sms_daily ? "true" : "false")
      toggle.setAttribute("data-backorder-organization-param", sub.organization)
      toggle.setAttribute("data-backorder-voivodeship-param", sub.voivodeship ?? "")

      if (isInsurance) {
        toggle.setAttribute("data-backorder-category-param", sub.category)
        toggle.setAttribute("data-backorder-county-param", sub.county ?? "")
      } else {
        toggle.setAttribute("data-backorder-subcategory-param", sub.subcategory)
      }

      const deleteBtn = row.querySelector('[data-field="delete"]')
      if (deleteBtn) {
        deleteBtn.setAttribute("data-backorder-id-param", sub.id)
      }

      this.rowsTarget.appendChild(row)
    }
  }

  switchVertical(event) {
    const v = event.currentTarget.dataset.vertical
    if (this.hasInsuranceFormTarget) this.insuranceFormTarget.hidden = v !== "ubezpieczenia"
    if (this.hasBurFormTarget) this.burFormTarget.hidden = v !== "bur"
    for (const tab of this.verticalTabTargets) {
      tab.classList.toggle("is-active", tab.dataset.vertical === v)
    }
    this.#formError(null)
  }

  async subscribeInsurance() {
    const category = comboValue(this.categorySelectTarget)
    if (!category) {
      this.#formError("Wybierz kategorię.")
      return
    }
    const voivodeship = comboValue(this.insVoivSelectTarget)
    if (!voivodeship) {
      this.#formError("Wybierz województwo.")
      return
    }
    const body = {
      category: Number(category),
      voivodeship: Number(voivodeship),
      email_frequency: this.#emailFrequency(),
      sms_daily: this.#smsDaily(),
    }
    const county = this.hasCountySelectTarget ? comboValue(this.countySelectTarget) : ""
    if (county) body.county = Number(county)
    const organization = this.#chosenOrganization()
    if (organization) body.organization = organization
    const res = await sendJSON("POST", this.urlValue, body)
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się dodać powiadomienia."))
      return
    }
    this.#formError(null)
    await this.load()
    if (res.status === 200) this.#status("Masz już to powiadomienie — zmień jego ustawienia w wierszu poniżej.")
  }

  async subscribe() {
    const subcategory = this.subcatSelectTarget.value
    if (!subcategory) {
      this.#formError("Wybierz podkategorię.")
      return
    }
    const body = {
      subcategory: Number(subcategory),
      email_frequency: this.#emailFrequency(),
      sms_daily: this.#smsDaily(),
    }
    const voivodeship = comboValue(this.voivSelectTarget)
    if (voivodeship) body.voivodeship = Number(voivodeship)
    const organization = this.#chosenOrganization()
    if (organization) body.organization = organization
    const res = await sendJSON("POST", this.urlValue, body)
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się dodać powiadomienia."))
      return
    }
    this.#formError(null)
    await this.load()
    if (res.status === 200) this.#status("Masz już to powiadomienie — zmień jego ustawienia w wierszu poniżej.")
  }

  editPreferences(event) {
    const row = event.currentTarget.closest('[data-backorder-row]')
    const sub = this.subscriptions.get(row.dataset.backorderRow)
    const editor = row.querySelector('[data-field="editor"]')
    if (row.querySelector('[data-field="edit-save"]').disabled) return
    for (const other of this.rowsTarget.querySelectorAll('[data-field="editor"]')) other.hidden = true
    for (const radio of editor.querySelectorAll('[data-field="edit-email"]')) {
      radio.checked = radio.value === sub.email_frequency
    }
    editor.querySelector('[data-field="edit-sms"]').checked = Boolean(sub.sms_daily)
    editor.querySelector('[data-field="edit-error"]').hidden = true
    editor.hidden = false
  }

  cancelPreferences(event) {
    event.currentTarget.closest('[data-field="editor"]').hidden = true
  }

  async savePreferences(event) {
    const row = event.currentTarget.closest('[data-backorder-row]')
    const button = row.querySelector('[data-field="edit-save"]')
    if (button.disabled) return
    const editor = row.querySelector('[data-field="editor"]')
    const error = editor.querySelector('[data-field="edit-error"]')
    error.hidden = true
    button.disabled = true
    const res = await sendJSON("PATCH", `${this.urlValue}${row.dataset.backorderRow}/`, {
      email_frequency: editor.querySelector('[data-field="edit-email"]:checked').value,
      sms_daily: editor.querySelector('[data-field="edit-sms"]').checked,
    })
    button.disabled = false
    if (!res.ok) {
      error.textContent = errorDetail(res.data, "Nie udało się zapisać ustawień.")
      error.hidden = false
      return
    }
    await this.load()
    this.#status("Zapisano ustawienia powiadomienia.")
  }

  async toggleActive(event) {
    const { id, active, subcategory, voivodeship, organization, emailFrequency, smsDaily } = event.params
    let res
    if (active) {
      // Dezaktywacja = PATCH {is_active: false} (miękkie wyłączenie, odwracalne)
      res = await sendJSON("PATCH", `${this.urlValue}${id}/`, { is_active: false })
    } else if (event.params.category) {
      // Reaktywacja kształtu insurance — POST {category, voivodeship, county?, organization, ...}
      const body = {
        category: Number(event.params.category),
        voivodeship: Number(voivodeship),
        organization: Number(organization),
        email_frequency: emailFrequency ?? "daily",
        sms_daily: smsDaily === true || smsDaily === "true",
      }
      if (event.params.county) body.county = Number(event.params.county)
      res = await sendJSON("POST", this.urlValue, body)
    } else {
      // Reaktywacja kształtu BUR — POST {subcategory, voivodeship?, organization, ...}
      const body = {
        subcategory: Number(subcategory),
        organization: Number(organization),
        email_frequency: emailFrequency ?? "daily",
        sms_daily: smsDaily === true || smsDaily === "true",
      }
      if (voivodeship) body.voivodeship = Number(voivodeship)
      res = await sendJSON("POST", this.urlValue, body)
    }
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się zmienić powiadomienia."))
      return
    }
    await this.load()
  }

  deleteSubscription(event) {
    const { id } = event.params
    const btn = event.currentTarget
    if (btn.dataset.confirmPending === "true") {
      this.#doDelete(Number(id))
    } else {
      btn.dataset.confirmPending = "true"
      setActLabel(btn, "Na pewno usunąć?")
    }
  }

  // --- prywatne ---

  async #doDelete(id) {
    const row = this.rowsTarget.querySelector(`[data-backorder-row="${id}"]`)
    const res = await sendJSON("DELETE", `${this.urlValue}${id}/`)
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się usunąć powiadomienia."))
      return
    }
    row?.remove()
    if (!this.rowsTarget.firstElementChild) {
      this.#status("Brak powiadomień. Dodaj pierwsze powyżej.")
    }
  }

  #emailFrequency() {
    if (!this.hasEmailFreqGroupTarget) return "daily"
    const checked = [...this.emailFreqGroupTarget.querySelectorAll("input[type=radio]")].find(r => r.checked)
    return checked?.value ?? "daily"
  }

  #smsDaily() {
    return this.hasSmsDailyCheckTarget ? this.smsDailyCheckTarget.checked : false
  }

  async #loadDictionaries() {
    let failed = false
    const load = (url) => getAllJSON(url).catch(() => { failed = true; return [] })
    try {
      const fetches = [
        // Each unavailable dictionary leaves the other controls usable.
        load(`${this.subcategoriesUrlValue}?vertical=bur`),
        load(this.voivodeshipsUrlValue),
        this.organizationsUrlValue ? load(this.organizationsUrlValue) : Promise.resolve([]),
      ]
      if (this.categoriesUrlValue) {
        fetches.push(load(this.categoriesUrlValue + "?vertical=ubezpieczenia"))
      }
      const [subcategories, voivodeships, organizations, categories] = await Promise.all(fetches)
      for (const sub of subcategories) {
        // Label „Kategoria › Podkategoria" dla wierszy listy — ten sam helper co w autocomplete
        this.subcatNames.set(sub.id, formatSubcatLabel(sub))
      }
      for (const voivodeship of voivodeships) {
        this.voivNames.set(voivodeship.id, voivodeship.name)
      }
      const voivOptions = voivodeships.map((v) => ({ value: v.id, label: v.name }))
      this.organizations = organizations
      const pushes = []
      if (this.hasVoivSelectTarget) pushes.push(setComboOptions(this.application, this.voivSelectTarget, voivOptions))
      if (this.hasInsVoivSelectTarget) pushes.push(setComboOptions(this.application, this.insVoivSelectTarget, voivOptions))
      if (this.hasOrgSelectTarget && organizations.length > 1) {
        pushes.push(setComboOptions(
          this.application,
          this.orgSelectTarget,
          organizations.map((org) => ({ value: org.id, label: org.name })),
        ))
        if (this.hasOrgWrapTarget) this.orgWrapTarget.hidden = false
      }
      if (categories && this.hasCategorySelectTarget) {
        pushes.push(setComboOptions(
          this.application,
          this.categorySelectTarget,
          categories.map((cat) => ({ value: cat.id, label: cat.name })),
        ))
      }
      await Promise.all(pushes)
      if (failed) this.#formError("Nie udało się załadować słowników. Odśwież stronę.")
    } catch {
      this.#formError("Nie udało się załadować słowników. Odśwież stronę.")
    }
  }

  #chosenOrganization() {
    if (this.hasOrgSelectTarget && !this.orgSelectTarget.closest("[hidden]")) {
      const chosen = comboValue(this.orgSelectTarget)
      if (chosen) return Number(chosen)
    }
    // Jedna org: wyślij ją jawnie (spójne z auto po stronie API; personel z 1 org też zadziała).
    if (this.organizations?.length === 1) return Number(this.organizations[0].id)
    return null
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return
    this.formErrorTarget.hidden = message === null
    if (message !== null) this.formErrorTarget.textContent = message
  }

  #status(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = message === null
    if (message !== null) this.statusTarget.textContent = message
  }
}
